import React, { useEffect, useState, useRef } from 'react';
import PageContainer from '../../components/container/PageContainer';
import { Card, CardContent, TextField, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { LoadScript, Autocomplete } from '@react-google-maps/api';

const libraries = ['places'];
const mapContainerStyle = {
  height: '80vh',
  width: '100%',
};
const center = {
  lat: 23.6355,
  lng: 78.3434,
};
const options = {
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  restriction: {
    latLngBounds: {
      north: 37.0857,
      south: 6.5546,
      west: 68.1114,
      east: 97.3954,
    },
    strictBounds: false,
  },
};

const defaultPosition = { lat: 28.6139, lng: 77.209 }; // Default to Delhi

function CTUMapContainer() {
  const [address, setAddress] = useState('');
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(4.5);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapStatus, setMapStatus] = useState('Initializing...');
  const role = useSelector((state) => state.RoleReducer);

  useEffect(() => {
    // Wait for both Google Maps and DOM to be ready
    const checkReadyAndLoadScript = () => {
      const mapContainer = document.getElementById('mapCTU');

      if (window.google && window.google.maps && mapContainer) {
        loadCustomMapScript();
      } else {
        setTimeout(checkReadyAndLoadScript, 200);
      }
    };

    // Start checking after LoadScript has time to render
    setTimeout(checkReadyAndLoadScript, 500);

    // Clean up function
    return () => {
      // Remove scripts when component unmounts
      const existingScript = document.querySelector('script[src="mapctu.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place.geometry) {
      const newPosition = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setAddress(place.formatted_address);

      // Wait for the map to be initialized by mapctu.js, then change location
      setTimeout(() => {
        if (window.changeLocation && window.map) {
          window.changeLocation(newPosition.lat, newPosition.lng, false);
        }
      }, 500);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newPosition = { lat: latitude, lng: longitude };

          // Use reverse geocoding to check location
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: newPosition }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const country = results[0].address_components.find((component) =>
                component.types.includes('country')
              );

              if (country && country?.short_name === 'IN') {
                setAddress(results[0].formatted_address);

                // Call the changeLocation function from mapctu.js if available
                setTimeout(() => {
                  if (window.changeLocation && window.map) {
                    window.changeLocation(latitude, longitude, false);
                  }
                }, 500);
              } else {
                setAddress('Outside India, redirecting to Delhi');

                setTimeout(() => {
                  if (window.changeLocation && window.map) {
                    window.changeLocation(defaultPosition.lat, defaultPosition.lng, false);
                  }
                }, 500);
              }
            } else {
              setAddress('Failed to retrieve location data');
            }
            setLoading(false);
          });
        },
        (error) => {
          console.error('Error retrieving location:', error.message);
          setLoading(false);
        }
      );
    } else {
    }
  };

  function loadCustomMapScript() {
    setMapStatus('Loading map script...');

    // Always remove existing script and reload fresh on page reload
    const existingMapScript = document.querySelector('script[src="mapctu.js"]');
    if (existingMapScript) {
      existingMapScript.remove();

      // Clear existing map objects (safely)
      try {
        if (window.map) {
          delete window.map;
        }
      } catch (e) {
        window.map = null;
      }

      try {
        if (window.initMap) {
          delete window.initMap;
        }
      } catch (e) {
        window.initMap = null;
      }
    }

    var customScript = document.createElement('script');
    customScript.src = 'mapctu.js';
    customScript.async = true;

    customScript.onload = function () {
      setMapStatus('Map script loaded, initializing...');

      // Wait a moment for the script to be parsed
      setTimeout(() => {
        if (window.initMap) {
          try {
            window.initMap();

            // Check if map was actually created
            setTimeout(() => {
              if (window.map) {
                setMapLoading(false);
                setMapStatus('');
              } else {
                console.error('❌ Map object not found after new initMap');
                setMapStatus('Map object not created');
              }
            }, 1000);
          } catch (error) {
            console.error('❌ Error initializing map:', error);
            setMapStatus('Map initialization failed: ' + error.message);
          }
        } else {
          console.error('❌ initMap function not found in mapctu.js');
          setMapStatus('initMap function not found in mapctu.js');
        }
      }, 100);
    };

    customScript.onerror = function (error) {
      console.error('❌ Failed to load mapctu.js:', error);
      setMapStatus('Failed to load mapctu.js');
      setMapLoading(false);
    };

    document.head.appendChild(customScript);
  }

  return (
    <LoadScript googleMapsApiKey="AIzaSyCjjE3O2R56Rx5nd4vrF9Rj8Gbbl-ZK1NU" libraries={libraries}>
      <div style={{ marginTop: '5rem' }}>
        <PageContainer title="CTU Map" description="CTU Map with Search">
          <div style={{ textAlign: 'center', margin: '1rem', position: 'relative', top: '10px' }}>
            <h3>Adopt CTU</h3>
          </div>

          <Card sx={{ width: '90%', margin: 'auto', marginTop: '10px', marginBottom: '10px' }}>
            <CardContent>
              {/* Search Controls */}
              <Box mb={2}>
                <Autocomplete
                  onLoad={(ref) => {
                    autocompleteRef.current = ref;
                  }}
                  onPlaceChanged={onPlaceChanged}
                  options={{
                    componentRestrictions: { country: 'in' },
                  }}
                >
                  <TextField
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setAddress(e.target.value)}
                    label="Search places"
                    value={address}
                    sx={{ mb: 2 }}
                    placeholder="Search for places in India..."
                  />
                </Autocomplete>

                <LoadingButton
                  loading={loading}
                  variant="contained"
                  onClick={getCurrentLocation}
                  sx={{ mb: 2 }}
                >
                  Get Current Location
                </LoadingButton>
              </Box>

              {/* Original CTU Map - Let mapctu.js handle this */}
              <div
                id="mapCTU"
                style={{
                  marginTop: '0.1rem',
                  width: '100%',
                  height: '80vh',
                  border: '1px solid #d3d3d3',
                  backgroundColor: '#f5f5f5',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    color: '#666',
                  }}
                >
                  Loading map...
                </div>
              </div>
            </CardContent>
          </Card>

          <h3>Note : Map updated every 1 hour</h3>
        </PageContainer>
      </div>
    </LoadScript>
  );
}

export default CTUMapContainer;
